import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import moment from "moment";

import { IDog, IRallyProgress, IRallyLevelProgress, IEarnedTitle, IDogRunSummary, ISportProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import ManualRunEntryModal from "../ManualRunEntry/ManualRunEntryModal";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./RallyCard.scss";

// Defined outside the component so AG Grid always receives a stable function reference
const ActionsCellRenderer = (params: any) => {
  const run = params.data;
  if (!run) return null;

  if (run.verified) {
    return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
  }

  if (params.deletingRunIdRef?.current === run._id) {
    return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
  }

  const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
  if (!isManualEntry) return null;

  const options: IContextMenuOption[] = [
    {
      label: 'Edit',
      onClick: () => params.handleEditRunRef?.current?.(run)
    },
    {
      label: 'Delete',
      onClick: () => params.handleDeleteRunRef?.current?.(run._id)
    }
  ];

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <ContextMenu options={options} disablePortal />
    </div>
  );
};

interface IProps {
  dog: IDog;
  rallyData: IRallyProgress;
  onRunUpdated?: () => void;
}

const RallyCard: React.FC<IProps> = ({ dog, rallyData, onRunUpdated }) => {
  const runHistoryModal = useModal();
  const manualRunModal = useModal();
  const deleteConfirmModal = useModal();
  const [editingRun, setEditingRun] = useState<IDogRunSummary | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(rallyData.recent_runs || []);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  // Must be declared before any early returns to satisfy Rules of Hooks
  const handleEditRunRef = useRef<((run: IDogRunSummary) => void) | null>(null);
  const handleDeleteRunRef = useRef<((runId: string) => void) | null>(null);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  // Sync localRuns with rallyData when it changes
  useEffect(() => {
    setLocalRuns(rallyData.recent_runs || []);
  }, [rallyData.recent_runs]);

  // Safety check
  if (!rallyData || !rallyData.levels) {
    return (
      <div className="rallyCard sportCard">
        <div className="cardHeader">
          <div className="headerLeft">
            <h3>AKC Rally</h3>
          </div>
        </div>
        <div className="progressSection">
          <p>No Rally data available.</p>
        </div>
      </div>
    );
  }

  const handleAddRun = () => {
    setEditingRun(null);
    manualRunModal.toggle();
  };

  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    manualRunModal.toggle();
  };
  handleEditRunRef.current = handleEditRun;

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };
  handleDeleteRunRef.current = handleDeleteRun;

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const rallySport = response.sports.find((s: ISportProgress) => s.show_type === 'Rally Obedience');
      if (rallySport) {
        setLocalRuns(rallySport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      console.error('Error deleting run:', error);
      toast.show({
        title: "Delete Failed",
        content: "Unable to delete the run. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleRunSaved = async () => {
    manualRunModal.toggle();
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const rallySport = response.sports.find((s: ISportProgress) => s.show_type === 'Rally Obedience');
      if (rallySport) {
        setLocalRuns(rallySport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleCloseRunsModal = () => {
    runHistoryModal.toggle();
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Class',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Score',
      field: 'score',
      valueFormatter: (params) => params.value !== undefined ? params.value : '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Result',
      field: 'result_status',
      valueFormatter: (params) => params.value || '-',
      cellStyle: (params) => {
        if (params.value === Constants.run_result_status.qualified) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value === Constants.run_result_status.not_qualified) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Judge #',
      field: 'judge_number',
      valueFormatter: (params) => params.value || '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Judge Name',
      field: 'judge_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Event',
      field: 'event_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 200,
      flex: 2
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: {
        handleEditRunRef,
        handleDeleteRunRef,
        deletingRunIdRef,
      },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 100,
      flex: 1
    }
  ];

  // Sort levels in correct order
  const levelOrder = [
    Constants.dog_class_element_level.rally_novice,
    Constants.dog_class_element_level.rally_intermediate,
    Constants.dog_class_element_level.rally_advanced,
    Constants.dog_class_element_level.rally_excellent,
    Constants.dog_class_element_level.rally_master
  ];

  const sortedLevels = rallyData.levels.sort((a: IRallyLevelProgress, b: IRallyLevelProgress) => {
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });

  return (
    <div className="rallyCard sportCard">
      {/* Header */}
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>AKC Rally</h3>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{rallyData.earned_titles.length}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Qualified Runs</div>
              <div className="statValue">{rallyData.total_qualified}</div>
            </div>
            {rallyData.total_points > 0 && (
              <div className="statItem">
                <div className="statLabel">Points</div>
                <div className="statValue">{rallyData.total_points}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {rallyData.earned_titles && Array.isArray(rallyData.earned_titles) && rallyData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <div className="titleBadges">
            {rallyData.earned_titles.map((title: IEarnedTitle) => (
              <div 
                key={title.title_code} 
                className="titleBadge"
                style={{ backgroundColor: title.color_hex || '#8B5CF6' }}
              >
                <div className="badgeInfo">
                  <div className="badgeTitle">{title.title_code}</div>
                  <div className="badgeDate">{title.earned_date ? new Date(title.earned_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Section */}
      <div className="progressSection">
        <div className="progressVertical">
          {/* Basic Levels (RN, RI, RA, RE, RM) */}
          {sortedLevels.map((levelData: IRallyLevelProgress, index: number) => {
            const isEarned = levelData.title_earned;
            const titleCode = levelData.title_code;
            const levelName = levelData.level;
            
            // Extract just the class name from the full level string (e.g., "Rally Novice" -> "Novice")
            const shortLevelName = levelName.replace('Rally ', '');
            
            // Rally Master requires 2 judges, others don't
            const isRallyMaster = levelData.level === Constants.dog_class_element_level.rally_master;

            return (
              <div key={levelData.level} className="levelRow">
                <div className="levelInfo">
                  <div className={classNames("levelTitle", { earned: isEarned })}>
                    {titleCode ? (
                      <>
                        <strong>{titleCode}</strong> ({shortLevelName})
                      </>
                    ) : (
                      levelName
                    )}
                    {isEarned && <span className="earnedCheck"> ✓</span>}
                  </div>
                </div>
                
                <div className="levelProgress">
                  <div className="progressText">{levelData.qualified_count}/{levelData.required_legs} Legs</div>
                  {isRallyMaster && (
                    <div className="judgeText">{levelData.unique_judges}/{levelData.required_judges} Judges</div>
                  )}
                  {levelData.highest_score && (
                    <div className="bestScore">Best: {levelData.highest_score}</div>
                  )}
                </div>

                {index < sortedLevels.length - 1 && (
                  <div className="levelConnector">↓</div>
                )}
              </div>
            );
          })}

          {/* RAE Section */}
          {rallyData.rae && (
            <>
              <div className="levelConnector">↓</div>
              <div className="levelRow raeRow">
                <div className="levelInfo">
                  <div className={classNames("levelTitle", { earned: rallyData.rae.title_earned })}>
                    RAE
                  </div>
                  <div className="levelName">Advanced B + Excellent B</div>
                </div>
                
                <div className="levelProgress">
                  <div className="progressText">{rallyData.rae.dual_qs}/{rallyData.rae.required_dual_qs} Dual Qs</div>
                </div>

                {rallyData.rae.title_earned && (
                  <div className="earnedBadge">✓</div>
                )}
              </div>
            </>
          )}

          {/* RACH Section */}
          {rallyData.rach && (
            <>
              <div className="levelConnector">↓</div>
              <div className="levelRow rachRow">
                <div className="levelInfo">
                  <div className={classNames("levelTitle", { earned: rallyData.rach.title_earned })}>
                    {rallyData.rach.title_code}
                  </div>
                  <div className="levelName">Rally Champion</div>
                </div>
                
                <div className="levelProgress">
                  <div className="progressText">{rallyData.rach.triple_qs}/{rallyData.rach.required_triple_qs} Triple Qs</div>
                  <div className="judgeText">{rallyData.rach.total_points}/{rallyData.rach.required_total_points} Points</div>
                  <div className="judgeText">{rallyData.rach.master_points}/{rallyData.rach.required_master_points} Master Pts</div>
                </div>

                {rallyData.rach.title_earned && (
                  <div className="earnedBadge">✓</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="cardDivider"></div>

      {/* View Run History Link - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View & Edit Run History ({rallyData.total_runs || 0} runs)
          </button>
        </div>
      ) : rallyData.recent_runs && rallyData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View Run History ({rallyData.total_runs || 0} runs)
          </button>
        </div>
      ) : null}

      {/* Run History Modal */}
      {runHistoryModal.isShown && (
        <Modal
          isShown={runHistoryModal.isShown}
          hide={handleCloseRunsModal}
          className="runsHistoryModal"
          modalContent={
            <div className="runsModal">
              <h2>AKC Rally - Run History</h2>

              <div className="runsModalContent">
                <div className="runsSection">
                  <div className="runsSectionHeader">
                    <h4>All Runs</h4>
                  </div>
                  {!rallyData.recent_runs || rallyData.recent_runs.length === 0 ? (
                    <div className="noRuns">
                      <p>No runs recorded yet.</p>
                    </div>
                  ) : (
                    <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                      <AgGridReact
                        rowData={localRuns}
                        columnDefs={columnDefs}
                        domLayout='normal'
                        suppressCellFocus={true}
                        theme={themeQuartz}
                        getRowId={(params) => params.data._id}
                      />
                    </div>
                  )}
                </div>

                <div className="modalActions">
                  <Button onClick={handleAddRun}>
                    + Add Manual Run
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Manual Run Entry Modal */}
      {manualRunModal.isShown && (
        <ManualRunEntryModal
          isOpen={manualRunModal.isShown}
          onClose={manualRunModal.toggle}
          dog={dog}
          sportType={Constants.show_type.rally_obedience}
          sanctioningClub={Constants.sanctioning_club.AKC}
          onSuccess={handleRunSaved}
          editRun={editingRun as any}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isShown && (
        <Modal
          isShown={deleteConfirmModal.isShown}
          hide={deleteConfirmModal.toggle}
          className="deleteConfirmModal"
          modalContent={
            <div className="deleteConfirmModal">
              <h3>Delete Run?</h3>
              <p>Are you sure you want to delete this run? This action cannot be undone and will recalculate title progress.</p>
              <div className="modalActions">
                <Button variant="secondary" onClick={deleteConfirmModal.toggle}>
                  Cancel
                </Button>
                <Button variant="default" onClick={confirmDelete}>
                  Delete Run
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default RallyCard;

